import { useEffect, useMemo, useState } from 'react';
import { Receipt } from 'lucide-react';
import { Button, Card, CardBody, Skeleton } from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Pagination,
  type FilterCondition,
  type FilterField,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import type { InvoiceListParams, InvoiceRow } from '@/api/invoices';
import { useInvoices, useMergeInvoices } from '@/api/invoices';
import { paginationView } from '@/pages/customers/pagination-summary';
import { evaluateMergeEligibility } from '../list-configs/invoice-merge-eligibility';
import { InvoiceListToolbar, colId, type SectionColumn } from './invoice-list-toolbar';

const PAGE_SIZE = 20;

/** Debounce a fast-changing value (search box) to throttle server refetches. */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export interface InvoiceListSectionProps {
  columns: SectionColumn<InvoiceRow>[];
  filterFields?: FilterField[];
  onRowClick?: (row: InvoiceRow) => void;
  searchPlaceholder?: string;
  /**
   * Fixed baseline filters for a pre-filtered tab (e.g. Invoicable / Deposit).
   * Merged under the user's own filter conditions, which can further narrow.
   */
  presetFilters?: Record<string, unknown>;
}

/**
 * Server-paginated invoice list: Toolbar (search + Merge + Filters popover +
 * Display options) over a selectable DataTable with Pagination. Search (`q`),
 * applied filter conditions and page are pushed to the backend via `useInvoices`;
 * loading → Skeleton, error → Alert+retry, empty → EmptyState. Mirrors the
 * contract list section, adding row-selection driven invoice merge (validated,
 * confirmed before firing).
 */
export function InvoiceListSection({
  columns,
  filterFields,
  onRowClick,
  searchPlaceholder = 'Search invoices…',
  presetFilters,
}: InvoiceListSectionProps) {
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);
  // Applied-date range (backend `invoice_apply_startdate: [from, to]`). Datetime
  // strings from TimeRangePicker ("YYYY-MM-DD HH:mm:ss") are accepted verbatim.
  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({});
  // DataTable's row-selection state is an id→selected map (`RowSelectionState`).
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [visible, setVisible] = useState<Record<string, boolean>>(
    () => Object.fromEntries(columns.map((c) => [colId(c), true])),
  );

  const filters = useMemo(() => {
    // Preset (tab baseline) first, then user conditions can add/override, then date range.
    const base: Record<string, unknown> = { ...(presetFilters ?? {}) };
    Object.assign(base, conditionsToFilters(conditions));
    if (dateRange.from && dateRange.to) {
      base.invoice_apply_startdate = [dateRange.from, dateRange.to];
    }
    return base;
  }, [presetFilters, conditions, dateRange]);

  const params: InvoiceListParams = {
    page,
    page_size: PAGE_SIZE,
    q: query || undefined,
    filters: Object.keys(filters).length ? filters : undefined,
  };
  const { data, isLoading, isError, isFetching, refetch } = useInvoices(params);
  const merge = useMergeInvoices();

  const rows = data?.results ?? [];
  const pagination = data?.pagination;
  const pageCount = pagination?.total_pages ?? 1;
  const total = pagination?.total ?? rows.length;

  const visibleColumns = useMemo(
    () => columns.filter((c) => visible[colId(c)] !== false),
    [columns, visible],
  );

  // Resolve the selected row objects from the id→bool selection map. Selection
  // ids are the stringified invoice_id (see getRowId below).
  const selectedRows = useMemo(
    () => rows.filter((r) => selection[String(r.invoice_id)]),
    [rows, selection],
  );
  const eligibility = evaluateMergeEligibility(selectedRows);

  const onSearch = (value: string) => {
    setRawQuery(value);
    setPage(1);
  };

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  const applyDateRange = (from?: string, to?: string) => {
    setDateRange(from && to ? { from, to } : {});
    setPage(1);
  };

  const runMerge = () => {
    if (!eligibility.ok) return;
    merge.mutate(
      { invoice_ids: selectedRows.map((r) => r.invoice_id) },
      {
        onSuccess: () => {
          setSelection({});
          setConfirmOpen(false);
        },
      },
    );
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <InvoiceListToolbar
        columns={columns}
        filterFields={filterFields}
        searchPlaceholder={searchPlaceholder}
        query={rawQuery}
        onQueryChange={onSearch}
        conditions={conditions}
        filterOpen={filterOpen}
        onFilterOpenChange={setFilterOpen}
        onApplyFilters={applyFilters}
        dateFrom={dateRange.from}
        dateTo={dateRange.to}
        onDateRangeApply={applyDateRange}
        visible={visible}
        onToggleColumn={(id, checked) => setVisible((prev) => ({ ...prev, [id]: checked }))}
        selectedCount={selectedRows.length}
        onMerge={() => setConfirmOpen(true)}
      />

      {selectedRows.length >= 2 && !eligibility.ok ? (
        <Alert tone="warning" title="These invoices can't be merged">
          {eligibility.reason}
        </Alert>
      ) : null}

      {isError ? (
        <Alert
          tone="danger"
          title="Failed to load invoices"
          actions={
            <Button size="sm" variant="ghost" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : isLoading ? (
        <Card flat>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 24 }} />
            ))}
          </CardBody>
        </Card>
      ) : (
        <DataTable
          columns={visibleColumns}
          data={rows}
          getRowId={(r) => String(r.invoice_id)}
          enableSelection
          rowSelection={selection}
          onRowSelectionChange={setSelection}
          onRowClick={onRowClick}
          emptyState={
            <EmptyState
              icon={<Receipt size={24} />}
              title="No invoices found"
              description="Try adjusting your search or filters."
            />
          }
          footer={(() => {
            const view = paginationView(page, PAGE_SIZE, rows.length, total, pageCount);
            return (
              <Pagination
                page={page}
                pageCount={view.pageCount}
                onPageChange={setPage}
                summary={`${view.summary}${isFetching ? ' …' : ''}`}
              />
            );
          })()}
        />
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(open) => !merge.isPending && setConfirmOpen(open)}
        title="Merge invoices"
        description={
          eligibility.ok
            ? `Merge ${selectedRows.length} invoices into one? This combines their line items and cannot be undone.`
            : eligibility.reason
        }
        confirmLabel="Merge"
        destructive
        loading={merge.isPending}
        onConfirm={runMerge}
      />
    </div>
  );
}

/**
 * Fold FilterBuilder conditions into the backend `filters` object. Multiselect
 * values arrive comma-joined; select/text collapse to a single-item array so the
 * backend's `IN`-style decode still applies. Multiple conditions on one field
 * merge their values.
 */
function conditionsToFilters(conditions: FilterCondition[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const c of conditions) {
    if (!c.value) continue;
    const values = c.value.split(',').map((s) => s.trim()).filter(Boolean);
    out[c.field] = [...(out[c.field] ?? []), ...values];
  }
  return out;
}
